<script setup>
import { useStore } from 'vuex'
import PlotlyLineChart from '@/components/plotly-chart/PlotlyLineChart.vue'
import { computed, ref } from 'vue'

const store = useStore()

const props = defineProps({
  dataChart: {
    type: Array,
    required: true
  },
  refPlot: {
    type: String,
    required: true
  }
})

const activeLine = ref(null)
const indexActivePoint = ref(null)
const currentStoreNamespace = ref(null)

const color = computed(() => store.state[currentStoreNamespace.value]['color'])
const coordinates = computed(() => store.state[currentStoreNamespace.value]['coordinates'])

const currentTopoProfile = computed(() => store.state.topoProfile.currentItem)

const getChartYAxisRange = computed(() => (id) => {
  return store.getters['topoProfilePoint/getChartYAxisRange'](id)
})

const currentItemAnnotation = computed(() => {
  return activeLine.value !== null && indexActivePoint.value !== null
    ? {
        x: activeLine.value[indexActivePoint.value]?.coordinate[0],
        y: activeLine.value[indexActivePoint.value]?.coordinate[1],
        arrowhead: 6,
        ax: 36,
        ay: -24,
        bgcolor: 'rgba(255, 255, 255, 0.9)',
        arrowcolor: color.value,
        bordercolor: color.value,
        borderwidth: 1.3,
        borderpad: 3,
        text: `<b>(${
          activeLine.value[indexActivePoint.value].coordinate[0]
        }, ${activeLine.value[indexActivePoint.value].coordinate[1]})</b>`,
        font: {
          size: 12,
          color: color.value
        }
      }
    : null
})

const topoProfilePointItems = computed(() => store.state.topoProfilePoint.items)
const topoProfilePointOptions = computed(() => store.state.topoProfilePoint.options)

const nameAnnotations = computed(() => {
  let itemsAnnotate = topoProfilePointItems.value.filter((item) => item.properties.name)
  return itemsAnnotate.map((item) => {
    let displayName = getDisplayName(item.properties.name)
    displayName = displayName ? displayName : item.properties.name
    return {
      x: item.geometry.coordinates[0],
      y: item.geometry.coordinates[1],
      yshift: 10,
      yanchor: 'bottom',
      textangle: '90',
      bgcolor: 'white',
      text: displayName,
      showarrow: false,
      ay: 40,
      hideToggle: true
    }
  })
})

const getDisplayName = (name) => {
  return topoProfilePointOptions.value?.name.choices.find((choice) => choice.value === name)
    ?.display_name
}

const setActiveItem = ({ storeNamespace, index }) => {
  indexActivePoint.value = index
  currentStoreNamespace.value = storeNamespace
  activeLine.value = coordinates.value
  emit('clickPoint', {
    storeNamespace: storeNamespace,
    pointIndex: index,
    color: color.value
  })
}
const deleteActiveItem = () => {
  indexActivePoint.value = null
}
const clickPoint = (event) => {
  currentStoreNamespace.value = event.points[0].data.storeNamespace
  if (store.state[currentStoreNamespace.value]) {
    indexActivePoint.value = event.points[0].pointIndex
    activeLine.value = coordinates.value
    emit('clickPoint', {
      storeNamespace: currentStoreNamespace.value,
      pointIndex: event.points[0].pointIndex,
      color: event.points[0].data.line.color
    })
  } else {
    indexActivePoint.value = null
    activeLine.value = null
  }
}

const emit = defineEmits(['clickPoint'])

defineExpose({
  setActiveItem,
  deleteActiveItem,
  clickPoint
})
</script>

<template>
  <PlotlyLineChart
    class="fill-height"
    :responsive="false"
    :data="dataChart"
    :showAnimate="false"
    :trace-index="[0]"
    :annotations="[...nameAnnotations, currentItemAnnotation]"
    :yAxisRange="getChartYAxisRange(currentTopoProfile?.id)"
    :refPlot="refPlot"
    :ref="refPlot"
    :clickEvent="true"
    @action="clickPoint"
  ></PlotlyLineChart>
</template>
